/**
 * Участники и приглашения.
 *
 * §9.4: активные участники и непринятые приглашения — ДВА РАЗНЫХ списка,
 * приглашения ниже участников. Смешивать их нельзя: это разные сущности
 * с разным набором действий.
 *
 * §4.4: повторная отправка доступна только для PENDING и EXPIRED.
 * У принятого приглашения кнопки нет вообще, а не заблокированная.
 *
 * Прежде все действия здесь показывали «подключается вместе с backend»,
 * а список приглашений был зашит в код.
 */

import { Permission } from "@taskflow/contracts";
import { UserPlus, UserX, XCircle } from "lucide-react";
import { useState } from "react";

import { useUserDirectoryQuery } from "@taskflow/client-core/directory";
import { usePermission, type WsError } from "@taskflow/client-core";
import {
  Avatar,
  Button,
  ConfirmDialog,
  ErrorState,
  FormField,
  fullNameOf,
  IconButton,
  Input,
  Modal,
  type PickerOption,
  Select,
  SkeletonRows,
  Tag,
  toast,
} from "@/shared/ui";
import { RotateCw } from "@/shared/ui/motionIcons";

import {
  type BoardInvitation,
  type BoardMember,
  type BoardRole,
  useAddMemberMutation,
  useBoardInvitationsQuery,
  useBoardMembersQuery,
  useInviteToBoardMutation,
  useRemoveMemberMutation,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
  useSetMemberRoleMutation,
} from "@taskflow/client-core/boards";

const OWNER = "board_owner";
/** Значение выбора «никому не передавать». */
const UNASSIGN = "__unassign__";

export function MembersSection({
  boardId,
  permissions,
}: {
  boardId: string;
  permissions: readonly Permission[];
}) {
  const members = useBoardMembersQuery(boardId, { skip: boardId === "" });
  const canManage = usePermission(Permission.BOARD_MEMBERS, permissions);
  const canChangeRoles = usePermission(Permission.BOARD_ROLES, permissions);

  const [setRole] = useSetMemberRoleMutation();
  const [removeMember, { isLoading: removingNow }] = useRemoveMemberMutation();

  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<BoardMember | null>(null);
  const [reassignTo, setReassignTo] = useState<string>(UNASSIGN);

  const list = members.data?.data ?? [];
  const roles = members.data?.roles ?? [];
  const roleOptions: PickerOption[] = roles.map((role) => ({ id: role.id, label: role.name }));

  // §20.3: на активной доске обязан остаться хотя бы один руководитель.
  // По КОДУ роли: идентификатор — UUID, и сравнение с "board_owner"
  // прежде не срабатывало никогда.
  const owners = list.filter((member) => member.boardRoleCode === OWNER);
  const lastOwner = owners.length === 1 ? owners[0] : null;

  const changeRole = (member: BoardMember, boardRoleId: string) => {
    if (boardRoleId === member.boardRoleId) return;
    void setRole({ boardId, memberId: member.id, boardRoleId })
      .unwrap()
      .then(() => toast.success("Роль изменена", fullNameOf(member)))
      .catch((raw: unknown) => toast.error(raw as WsError, "Не удалось изменить роль"));
  };

  const confirmRemove = async () => {
    if (removing === null) return;
    try {
      await removeMember({
        boardId,
        memberId: removing.id,
        ...(reassignTo === UNASSIGN ? {} : { reassignTo }),
      }).unwrap();
      toast.success("Участник удалён с доски", fullNameOf(removing));
      setRemoving(null);
    } catch (raw) {
      toast.error(raw as WsError, "Не удалось удалить участника");
    }
  };

  const replacements: PickerOption[] = [
    { id: UNASSIGN, label: "Снять назначения" },
    ...list
      .filter((member) => removing !== null && member.id !== removing.id)
      .map((member) => ({ id: member.userId, label: fullNameOf(member), hint: member.boardRoleName })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <section>
        <div className="mb-2.5 flex items-center justify-between">
          <h2 className="text-[13px] font-extrabold text-fg">
            Участники
            {members.data !== undefined && (
              <span className="ml-1.5 text-faint tabular-nums">{list.length}</span>
            )}
          </h2>
          {canManage && (
            <Button size="sm" variant="secondary" icon={UserPlus} onClick={() => setAdding(true)}>
              Добавить участника
            </Button>
          )}
        </div>

        {members.isLoading && <SkeletonRows rows={3} />}

        {members.error !== undefined && (
          <ErrorState
            message={(members.error as WsError).data.message}
            onRetry={() => void members.refetch()}
          />
        )}

        {members.data !== undefined && (
          <ul className="flex flex-col gap-1.5">
            {list.map((member) => {
              const isLastOwner = lastOwner?.id === member.id;
              return (
                <li
                  key={member.id}
                  className="flex items-center gap-3 rounded-md border border-line px-3 py-2"
                >
                  <Avatar person={member} size="md" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold text-fg">
                      {fullNameOf(member)}
                    </p>
                    <p className="truncate text-[11px] text-faint">
                      {member.position ?? "Должность не указана"} · в доске с{" "}
                      {member.joinedAtLabel}
                      {member.openAssignments > 0 &&
                        ` · открытых задач: ${String(member.openAssignments)}`}
                    </p>
                  </div>

                  <div className="w-52 shrink-0">
                    {canChangeRoles && !isLastOwner ? (
                      <Select
                        label={`Роль участника ${fullNameOf(member)}`}
                        placeholder="Роль"
                        options={roleOptions}
                        value={member.boardRoleId}
                        onChange={(value) => changeRole(member, value)}
                      />
                    ) : (
                      <Tag tone="neutral">{member.boardRoleName}</Tag>
                    )}
                  </div>

                  {canManage && (
                    <IconButton
                      icon={UserX}
                      label={`Удалить ${fullNameOf(member)} с доски`}
                      variant="danger"
                      disabled={isLastOwner}
                      title={isLastOwner ? "Нельзя удалить последнего руководителя доски" : undefined}
                      onClick={() => {
                        setReassignTo(UNASSIGN);
                        setRemoving(member);
                      }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* §9.4: приглашения — отдельный список НИЖЕ участников. */}
      <section className="border-t border-line pt-5">
        <h2 className="mb-2.5 text-[13px] font-extrabold text-fg">Непринятые приглашения</h2>
        <InvitationList boardId={boardId} canManage={canManage} />
      </section>

      {adding && (
        <AddMemberDialog
          boardId={boardId}
          members={list}
          roles={roles}
          onClose={() => setAdding(false)}
        />
      )}

      {/* §25.4: роль и число открытых задач видны ДО удаления, решение
          о задачах принимается здесь же — одной транзакцией на сервере. */}
      <Modal
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title="Удалить участника с доски"
        width="sm"
        dismissable={!removingNow}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoving(null)} disabled={removingNow}>
              Отмена
            </Button>
            <Button variant="danger" loading={removingNow} onClick={() => void confirmRemove()}>
              Удалить с доски
            </Button>
          </>
        }
      >
        {removing !== null && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted">
              {fullNameOf(removing)} ({removing.boardRoleName}) потеряет доступ к доске
              немедленно. История и авторство сохранятся.
            </p>
            {removing.openAssignments > 0 ? (
              <FormField
                label={`Открытых задач: ${String(removing.openAssignments)}. Кому их передать?`}
              >
                {(props) => (
                  <Select
                    {...props}
                    label="Кому передать задачи"
                    placeholder="Выберите участника"
                    options={replacements}
                    value={reassignTo}
                    onChange={setReassignTo}
                  />
                )}
              </FormField>
            ) : (
              <p className="text-[13px] text-faint">Открытых задач у участника нет.</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

/** Добавить сотрудника из справочника или пригласить по почте. */
function AddMemberDialog({
  boardId,
  members,
  roles,
  onClose,
}: {
  boardId: string;
  members: BoardMember[];
  roles: BoardRole[];
  onClose: () => void;
}) {
  const directory = useUserDirectoryQuery({});
  const [addMember, { isLoading: addingNow }] = useAddMemberMutation();
  const [invite, { isLoading: invitingNow }] = useInviteToBoardMutation();

  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const effectiveRole = roleId ?? roles.find((role) => role.code === "member")?.id ?? null;
  const taken = new Set(members.map((member) => member.userId));
  const candidates: PickerOption[] = (directory.data?.data ?? [])
    .filter((person) => !taken.has(person.id))
    .map((person) => ({
      id: person.id,
      label: `${person.lastName} ${person.firstName}`,
      hint: person.position ?? person.departmentName,
    }));
  const busy = addingNow || invitingNow;

  const submit = async () => {
    setError(null);
    if (effectiveRole === null) {
      setError("Выберите роль");
      return;
    }
    const normalized = email.trim().toLowerCase();
    if (userId === null && normalized === "") {
      setError("Выберите сотрудника или укажите почту для приглашения");
      return;
    }
    try {
      if (userId !== null) {
        await addMember({ boardId, userId, boardRoleId: effectiveRole }).unwrap();
        toast.success("Участник добавлен");
      } else {
        if (!normalized.includes("@")) {
          setError("Укажите корректный адрес почты");
          return;
        }
        await invite({ boardId, email: normalized, boardRoleId: effectiveRole }).unwrap();
        toast.success("Приглашение отправлено", normalized);
      }
      onClose();
    } catch (raw) {
      setError((raw as WsError).data.message);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Добавить участника"
      width="sm"
      dismissable={!busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {userId === null && email.trim() !== "" ? "Отправить приглашение" : "Добавить"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <FormField label="Сотрудник">
          {(props) => (
            <Select
              {...props}
              label="Сотрудник"
              placeholder={directory.isLoading ? "Загрузка…" : "Выберите сотрудника"}
              options={candidates}
              value={userId}
              onChange={(value) => {
                setUserId(value);
                setEmail("");
              }}
            />
          )}
        </FormField>

        <FormField label="Или пригласить по почте" hint="Если человека ещё нет в системе">
          {(props) => (
            <Input
              {...props}
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setUserId(null);
              }}
            />
          )}
        </FormField>

        <FormField label="Роль на доске" required>
          {(props) => (
            <Select
              {...props}
              label="Роль на доске"
              placeholder="Выберите роль"
              options={roles.map((role) => ({ id: role.id, label: role.name }))}
              value={effectiveRole}
              onChange={setRoleId}
            />
          )}
        </FormField>

        {error !== null && (
          <p role="alert" className="text-[13px] font-semibold text-danger">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function InvitationList({ boardId, canManage }: { boardId: string; canManage: boolean }) {
  const invitations = useBoardInvitationsQuery(boardId, { skip: boardId === "" || !canManage });
  const [resend] = useResendInvitationMutation();
  const [revoke, { isLoading: revokingNow }] = useRevokeInvitationMutation();
  const [revoking, setRevoking] = useState<BoardInvitation | null>(null);

  if (!canManage) {
    return (
      <p className="text-[13px] text-faint">
        Список приглашений доступен участникам с правом управления составом
      </p>
    );
  }
  if (invitations.isLoading) return <SkeletonRows rows={2} />;
  if (invitations.error !== undefined) {
    return (
      <ErrorState
        message={(invitations.error as WsError).data.message}
        onRetry={() => void invitations.refetch()}
      />
    );
  }

  const rows = invitations.data?.data ?? [];

  return (
    <>
      {rows.length === 0 ? (
        <p className="text-[13px] text-faint">Непринятых приглашений нет</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {rows.map((invitation) => (
            <li
              key={invitation.id}
              className="flex items-center gap-3 rounded-md border border-line px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-fg">{invitation.email}</p>
                <p className="text-[11px] text-faint">
                  {invitation.roleName || "Роль не указана"} · отправлено {invitation.sentAtLabel} ·
                  действует до {invitation.expiresAtLabel}
                </p>
              </div>

              <Tag tone={invitation.status === "EXPIRED" ? "warning" : "info"} size="sm">
                {invitation.status === "EXPIRED" ? "истекло" : "ожидает"}
              </Tag>

              {/* §4.4: повторная отправка — только PENDING и EXPIRED; других
                  статусов в этом списке нет по построению. */}
              <IconButton
                icon={RotateCw}
                label={`Отправить приглашение ${invitation.email} повторно`}
                onClick={() =>
                  void resend({ boardId, inviteId: invitation.id })
                    .unwrap()
                    .then(() => toast.success("Приглашение отправлено повторно", invitation.email))
                    .catch((raw: unknown) =>
                      toast.error(raw as WsError, "Не удалось отправить повторно"),
                    )
                }
              />
              <IconButton
                icon={XCircle}
                label={`Отозвать приглашение ${invitation.email}`}
                variant="danger"
                onClick={() => setRevoking(invitation)}
              />
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={revoking !== null}
        title="Отозвать приглашение?"
        description={
          revoking === null ? "" : `Ссылка из письма для ${revoking.email} перестанет работать.`
        }
        confirmLabel="Отозвать"
        tone="danger"
        loading={revokingNow}
        onConfirm={() => {
          if (revoking === null) return;
          void revoke({ boardId, inviteId: revoking.id })
            .unwrap()
            .then(() => {
              toast.success("Приглашение отозвано", revoking.email);
              setRevoking(null);
            })
            .catch((raw: unknown) => toast.error(raw as WsError, "Не удалось отозвать"));
        }}
        onCancel={() => setRevoking(null)}
      />
    </>
  );
}
